import { Box, Tooltip } from "@material-ui/core";
import { Add } from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";
import clsx from "clsx";
import _ from "lodash";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";

import { LOAD_EVENTS } from "../../../reducers/events";
import { StoreState } from "../../../store";
import { LoadedEventDefinition } from "../../../types/global";
import { handleToggleDestination } from "../../api";
import { DestinationDetail } from "../../types";
import {
  getDestinationsForEvent,
  isDestinationEnabledForEvent,
} from "../../utils";
import DestinationsMenu from "../BulkEdit/DestinationsMenu";
import useArrayOfDestinations from "../../../hooks/useArrayOfDestinations";
import {
  RequestableSaasDestination,
  SaaSDestination,
  WarehouseDestination,
} from "../../../destinations/destinations";

// Here, we memoize only the eventId and destinationId to make sure we're always getting the same
// debounce function back
const memoizedDebouncedHandleToggleDestination = _.memoize(
  (eventId: string, destinationId: string) =>
    // now we simply debounce to prevent multiple calls and potential issues with racing conditions
    // creating a mismatch between FE and BE.
    _.debounce(
      (state: boolean) =>
        handleToggleDestination(eventId, destinationId, state),
      1000
    ),
  (...args) => args.join("_")
);

const useStyle = makeStyles(() => ({
  logoContainer: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "white",
    border: "1px solid rgba(63, 81, 181, 0.1)",
  },
}));

interface DestinationsCellProps {
  event: LoadedEventDefinition;
  configurableDestinations: Record<
    string,
    | SaaSDestination
    | RequestableSaasDestination
    | WarehouseDestination
    | undefined
  >;
}

const Destination = React.memo(function ({
  Icon,
  name,
  index,
  tooltip,
}: DestinationDetail & { index: number; tooltip?: boolean }) {
  const classes = useStyle();
  if (!Icon) {
    return null;
  }

  return tooltip ? (
    <Tooltip placement="top" title={name} key={name}>
      <Box
        className={clsx(classes.logoContainer)}
        style={{ transform: `translateX(-${index * 8}px)` }}
      >
        <Icon width={20} height={20} />
      </Box>
    </Tooltip>
  ) : (
    <Box
      className={clsx(classes.logoContainer)}
      style={{ transform: `translateX(-${index * 8}px)` }}
    >
      <Icon width={20} height={20} />
    </Box>
  );
});

export default function DestinationsCell({
  event,
  configurableDestinations,
}: DestinationsCellProps) {
  const classes = useStyle();
  const history = useHistory();
  const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);
  const [showDestinationsDropdown, setShowDestinationsDropdown] = useState(
    false
  );

  const dispatch = useDispatch();
  const { eventDefs } = useSelector((state: StoreState) => state.eventDefs);
  const allDestinations = useArrayOfDestinations();

  const MENU_ID = "manage-destinations-menu";

  const toggleDestination = React.useCallback(
    (destinationId: string, checked: boolean) => {
      if (!event) {
        return;
      }

      dispatch({
        type: LOAD_EVENTS,
        payload: eventDefs.map((ev) =>
          ev.id !== event.id
            ? ev
            : _.extend({}, event, {
                enabled_dests: checked
                  ? _.extend({}, event.enabled_dests, {
                      [destinationId]: true,
                    })
                  : _.omit(event.enabled_dests, destinationId),
              })
        ),
      });

      memoizedDebouncedHandleToggleDestination(
        event.id,
        destinationId
      )(checked);
    },
    [event, eventDefs, dispatch]
  );

  const destinations = React.useMemo(
    () =>
      event
        ? _.sortBy(
            getDestinationsForEvent(
              allDestinations,
              configurableDestinations,
              event
            ),
            (d) => d?.name
          )
        : [],
    [event, allDestinations, configurableDestinations]
  );

  if (!event) {
    return null;
  }

  const onClickAddDestinations = (e) => {
    // we stop propagation of event to prevent that
    // clicks on buttons trigger clicks on the row
    e.stopPropagation();

    if (allDestinations.length > 0) {
      setShowDestinationsDropdown(!showDestinationsDropdown);
    } else {
      history.push("/destinations/apps");
    }
  };

  const ManageDestinationsIcon = () => {
    return (
      <Tooltip placement="top" title="Manage Destinations">
        <Box
          component="button"
          className={clsx(classes.logoContainer)}
          style={{
            transform: `translateX(-4px)`,
          }}
          aria-controls={MENU_ID}
          aria-haspopup={true}
          onClick={onClickAddDestinations}
        >
          <Add width={20} height={20} />
        </Box>
      </Tooltip>
    );
  };

  const AddNewDestinationsIcon = () => {
    return (
      <Tooltip placement="top" title="Add Destinations">
        <Box
          component="button"
          className={clsx(classes.logoContainer)}
          style={{
            transform: `translateX(-4px)`,
          }}
          aria-controls={MENU_ID}
          aria-haspopup={true}
          onClick={onClickAddDestinations}
        >
          <Add width={20} height={20} />
        </Box>
      </Tooltip>
    );
  };

  return (
    // the ref is used as anchor element
    // to position the dropdown menu
    <div ref={setAnchorEl} style={{ display: "flex" }}>
      {event.segment_compatible ? (
        <Tooltip
          placement="top"
          title={
            <span>
              {"Can't manage hardcoded destinations for event."}
              <br />
              {"Click to edit this setting."}
            </span>
          }
        >
          <Box display="flex" alignItems="center">
            {destinations.length > 0 ? (
              <ManageDestinationsIcon />
            ) : (
              <AddNewDestinationsIcon />
            )}
            {destinations.map((data, i) => (
              <Destination key={data.name + i} {...data} index={i} />
            ))}
          </Box>
        </Tooltip>
      ) : (
        <Box display="flex" alignItems="center">
          {destinations.length > 0 ? (
            <ManageDestinationsIcon />
          ) : (
            <AddNewDestinationsIcon />
          )}
          {destinations.map((data, i) => (
            <Destination key={data.name + i} {...data} index={i} tooltip />
          ))}
        </Box>
      )}

      <DestinationsMenu
        menuId={MENU_ID}
        anchorEl={anchorEl}
        onToggle={toggleDestination}
        selectedDestinations={_.keys(destinations)
          .filter((d) =>
            isDestinationEnabledForEvent(destinations[d], d, event)
          )
          .map((d) => destinations[d].id)}
        configurableDestinations={configurableDestinations}
        segmentCompatible={event.segment_compatible}
        onClose={() => setShowDestinationsDropdown(false)}
        show={showDestinationsDropdown}
        header={`Edit Destinations for ${event.name}`}
      />
    </div>
  );
}
